import { DocumentFilter, type ReferenceData } from '../api/client'
import type { SearchOption } from './SearchSelect'

/**
 * Reference data shaped for the pickers.
 *
 * In their own module rather than beside a component: a file that exports both a
 * component and a plain function breaks React Fast Refresh, so editing either one
 * reloads the whole page instead of preserving form state — which on the add
 * screen means losing everything typed so far.
 */

/**
 * Machinery types are flagged, not filtered out. The user needs to see that the
 * type exists and is excluded from the register, rather than wondering why it is
 * missing from the list.
 */
export function vehicleTypeOptions(reference: ReferenceData | undefined): SearchOption[] {
  return (reference?.vehicleTypes ?? []).map((t) => ({
    value: t.vehicleTypeId!,
    label: t.name ?? '',
    hint: t.isMachinery ? 'machinery' : undefined,
  }))
}

export function siteOptions(reference: ReferenceData | undefined): SearchOption[] {
  return (reference?.sites ?? []).map((s) => ({ value: s.siteId!, label: s.name ?? '' }))
}

/**
 * The certificates-on-file filter.
 *
 * Values are composite — "2:5" is "missing a PUC certificate" — because the
 * filter carries two parameters and a dropdown offers one. The per-obligation
 * rows come from `acceptsDocument`, so Permit and Goods Tax never appear and
 * adding one later is an UPDATE rather than a deployment.
 */
export function documentFilterOptions(
  reference: ReferenceData | undefined,
): SearchOption<string>[] {
  return [
    { value: `${DocumentFilter.HasNone}:`, label: 'Missing every certificate' },
    { value: `${DocumentFilter.HasAny}:`, label: 'Has at least one certificate' },
    ...(reference?.complianceTypes ?? [])
      .filter((t) => t.acceptsDocument)
      .map((t) => ({
        value: `${DocumentFilter.HasNone}:${t.complianceTypeId}`,
        label: `Missing ${t.displayName} certificate`,
      })),
  ]
}

/** The four states an expiry can be in, as picker rows. */
export function statusOptions(): SearchOption[] {
  return [
    { value: 4, label: 'Unknown', hint: 'not on record' },
    { value: 1, label: 'Has a date' },
    { value: 2, label: 'Never expires', hint: 'LTT / OTT' },
    { value: 3, label: 'Not applicable' },
  ]
}
